import {BehaviorSubject, combineLatest, debounceTime, defer, delayWhen, filter, map, Observable, shareReplay, skipUntil, startWith, Subject, switchMap, tap, withLatestFrom} from 'rxjs';
import {assetsApi} from '@/api/assets';
import {div, gt, gte, lte, pow10} from '@/helper/num';
import {rsaVerify} from '@/helper/crypto';
import {mc} from '@/core';
import {userInfo$} from '@/store/user';
import {browser$} from '@/streams/browser';
import {connector$} from '@/streams/wallet-connect';
import {getSupportNetworks} from '../../utils';
import {trackAddressCryptoError} from '../../track';
import {DepositToAccountType} from '../utils';
import {formatNetworks} from './format-data';
import {componentStatus$, useSafeNetworkEffect, useInitDataEffect, useSafeDepositAccountType, useSafeDepositAmountEffect, setDefaultData$} from './effect';
import type {DepositAddressResult, FormatedResult, Network, Token, Currency$, Network$, CurrentNetworkOptions$, DepositAccountType$, CurrentDepositAddress$} from './shared';

export * from './submit';
export {componentStatus$, setDefaultData$};

/** 基础数据  */
export const currency$: Currency$ = new BehaviorSubject('');
export const network$: Network$ = new BehaviorSubject('');
export const amount$ = new BehaviorSubject(0);
export const depositAccountType$: DepositAccountType$ = new BehaviorSubject(DepositToAccountType.spot);

/** 加载以及格式化currency数据 */
export const loadData$ = new Subject();
export const formatedData$: Observable<FormatedResult> = loadData$.pipe(
  skipUntil(browser$),
  withLatestFrom(
    connector$.pipe(
      map((connector) => connector.address),
      filter(Boolean)
    )
  ),
  map(([, address]) => ({needBalance: true, address})),
  switchMap(getSupportNetworks),
  map(formatNetworks),
  startWith({
    originData: [],
    networkCurrencyOptionsMap: {},
    networkSupportCurrenciesMap: {},
    currencyNetworkMap: {},
    tokenMap: {},
    contractAddressTokenMap: {},
    chainIdNetworkMap: {},
    chainNameNetworkMap: {},
    currencyNameCurrencyMap: {},
  }),
  shareReplay(1)
);

const invalidData$ = formatedData$.pipe(filter((supportCurrencies) => Object.keys(supportCurrencies.chainIdNetworkMap)?.length > 0));
const invalidCurrency$ = currency$.pipe(filter(Boolean));
const invalidNetwork$ = network$.pipe(filter(Boolean));

/**
 * 当前可以选择的currency列表
 * currency数量不变，但是顺序会变，优先展示当前链上的currency
 */
export const currentCurrencyOptions$ = combineLatest([invalidNetwork$, invalidData$]).pipe(
  map(([network, formatedCurrencies]) => formatedCurrencies.networkCurrencyOptionsMap[network] || []),
  startWith([]),
  shareReplay(1)
);

/** 当前可以选择的network列表  */
export const currentNetworks$ = combineLatest([invalidCurrency$, invalidData$]).pipe(
  map(([currency, formatedCurrencies]) => formatedCurrencies.currencyNetworkMap[currency] || []),
  shareReplay(1)
);
export const currentNetworkOptions$: CurrentNetworkOptions$ = currentNetworks$.pipe(
  filter((currentNetworks) => currentNetworks.length > 0),
  map((currentNetworks) => currentNetworks.map(({displayName: text, extNetWorkId: value, chainId: inferChainId, chainName}) => ({text, value, inferChainId, chainName}))),
  startWith([]),
  shareReplay(1)
);

/** 当前选中的token  */
export const token$: Observable<Token> = combineLatest({
  currency: invalidCurrency$,
  network: invalidNetwork$,
  tokenMap: invalidData$.pipe(map((formatedCurrencies) => formatedCurrencies.tokenMap)),
}).pipe(
  map(({currency, network, tokenMap}) => tokenMap[`${currency}_${network}`]),
  filter(Boolean)
);
export const tokenAddress$ = token$.pipe(map((token) => token.contractName || ''));

/** 当前token可供入金的最小最大值  */
export const depositLimit$ = token$.pipe(
  map(({balance, showBalance}) => ({min: 0, max: showBalance ? Number(balance || 0) : 1_000_000_000_000})),
  startWith({min: 0, max: 0})
);

/** 当前token入金的精度配置  */
export const amountInputConfig$ = token$.pipe(
  map(({withdrawPrecision}) => {
    const tickSizeEv = div(1, pow10(withdrawPrecision));
    return {
      precision: withdrawPrecision,
      tickSizeEv,
    };
  })
);

/**
 * 判断当前currency是否支持入金到合约账户
 */
export const currentCurrencySupportDepositToContract$ = invalidCurrency$.pipe(
  switchMap((currency) =>
    defer(() => getDepositsCheckToContract(currency)).pipe(
      map((depositContract) => ({loading: false, data: !!depositContract})),
      startWith({loading: true, data: false})
    )
  ),
  startWith({loading: true, data: false}),
  shareReplay(1)
);

const networkMap$ = invalidData$.pipe(
  map((data) =>
    data.originData.reduce((pre, item) => {
      pre[item.extNetWorkId] = item;
      return pre;
    }, {} as Record<Network['extNetWorkId'], Network>)
  ),
  shareReplay(1)
);
export const currentDepositAddress$: CurrentDepositAddress$ = combineLatest([invalidCurrency$, invalidNetwork$]).pipe(
  delayWhen(() => networkMap$),
  withLatestFrom(networkMap$, invalidData$),
  debounceTime(10),
  map(([[currency, networkId], networkMap, invalidData]) => ({
    chainId: networkMap[networkId]?.chainId,
    chainName: networkMap[networkId]?.chainName,
    currencyCode: invalidData.currencyNameCurrencyMap[currency]?.currencyCode,
    currency,
    networkId,
  })),
  switchMap(({chainId, chainName, currencyCode, currency, networkId}) =>
    defer(() => getDepositAddressInSign({chainName, currency})).pipe(map((data) => ({...data, currency, currencyCode, chainId, networkId})))
  ),
  shareReplay(1)
);

export const currentReceiveAddress$ = currentDepositAddress$.pipe(
  withLatestFrom(userInfo$),
  switchMap(([depositAddress, userInfo]) => {
    const {address, sign, tag} = depositAddress;
    const data = `${userInfo.userId}${address}${tag}`;
    return defer(() => rsaVerify({data, signature: sign})).pipe(
      tap((verified) => {
        if (!verified) {
          mc.send('toast', 'error', {message: 'The billing address may be incorrect'});
          trackAddressCryptoError({...depositAddress, data});
        }
      }),
      map((verified) => (verified ? address : ''))
    );
  }),
  shareReplay(1)
);

/** 当前数据是否有效 */
export const isValid$ = combineLatest({
  currency: currency$,
  network: network$,
  amount: amount$,
  depositAccountType: depositAccountType$,
  depositLimit: depositLimit$,
  currentReceiveAddress: currentReceiveAddress$,
}).pipe(
  map(({currency, network, amount, depositAccountType, depositLimit, currentReceiveAddress}) => {
    if (!currency || !network || !currentReceiveAddress) {
      return false;
    }
    if (!Object.values(DepositToAccountType).includes(depositAccountType)) {
      return false;
    }
    return gte(amount, depositLimit.min) && gt(amount, 0) && lte(amount, depositLimit.max);
  })
);

useSafeNetworkEffect({network$, currentNetworkOptions$});
useInitDataEffect({invalidData$, currency$, network$});
useSafeDepositAccountType({depositAccountType$, currentDepositAddress$, currency$, network$});
useSafeDepositAmountEffect({currency$, network$, amount$, depositLimit$});

async function getDepositAddressInSign({chainName, currency}): Promise<DepositAddressResult> {
  const {data, error} = await assetsApi.getDepositAddressInSign({chainName, currency});
  if (error) {
    mc.send('toast', 'error', error);
    return {} as DepositAddressResult;
  }
  if (data) {
    return data;
  }
}

async function getDepositsCheckToContract(currencyName) {
  if (!currencyName) {
    return false;
  }
  const {data} = await assetsApi.getDepositsCheckToContract({currencyName});
  return data && data.depositContract;
}
